import { z } from 'zod';

/**
 * Cursor pagination, not offset pagination.
 *
 * Licensing collections are append-heavy and are read while they are being written
 * to. Offset pagination silently skips and duplicates rows under those conditions,
 * and `OFFSET 50000` degrades exactly when a customer has grown enough to care.
 * The cursor is an opaque string: making it opaque is what lets us change the
 * underlying keyset later without a breaking change.
 */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

export const pageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  cursor: z.string().min(1).max(512).optional(),
});

export type PageQuery = z.infer<typeof pageQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    object: z.literal('list'),
    data: z.array(item),
    has_more: z.boolean(),
    next_cursor: z.string().nullable(),
  });
}

export interface Page<T> {
  object: 'list';
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

/**
 * A page of audit events, optionally narrowed to one resource.
 *
 * Both parts are optional and only meaningful together — the route treats a
 * half-supplied pair as no filter at all rather than guessing which half to
 * honour.
 */
export const auditQuerySchema = pageQuerySchema.extend({
  resource_type: z.string().min(1).max(64).optional(),
  resource_id: z.string().min(1).max(64).optional(),
});

export type AuditQuery = z.infer<typeof auditQuerySchema>;
